import React from "react";
import { useQuery } from "react-query";
import {
  FiArrowDown,
  FiCheckCircle,
  FiChevronLeft,
  FiChevronRight,
  FiClock,
  FiCreditCard,
  FiInbox,
  FiRefreshCw,
  FiXCircle,
} from "react-icons/fi";
import useLocalStorage from "helpers/useLocalStorage";
import { donateWithdrawGetMany } from "services/withdraw.service";
import { WithdrawRequest, WithdrawRequestStatus } from "pages/withdraw/withdraw.types";
import {
  formatCurrency,
  getWithdrawNetAmount,
  normalizeWithdrawRequests,
} from "pages/withdraw/withdraw.utils";

const HISTORY_FETCH_LIMIT = 250;
const HISTORY_PAGE_SIZE = 8;

type StatusFilter = "ALL" | "PENDING" | "SUCCESS" | "FAILED_OR_CANCELED";

type WithdrawHistoryQueryData = {
  items: WithdrawRequest[];
  totalCount?: number;
};

const statusMeta: Record<
  WithdrawRequestStatus,
  { label: string; className: string; icon: React.ComponentType<{ className?: string }> }
> = {
  PENDING: {
    label: "ກຳລັງດຳເນີນ",
    className: "border-cyan-200 bg-cyan-50 text-cyan-700",
    icon: FiClock,
  },
  SUCCESS: {
    label: "ສຳເລັດ",
    className: "border-emerald-200 bg-emerald-50 text-emerald-700",
    icon: FiCheckCircle,
  },
  FAILED: {
    label: "ບໍ່ສຳເລັດ",
    className: "border-red-200 bg-red-50 text-red-700",
    icon: FiXCircle,
  },
  CANCELED: {
    label: "ຍົກເລີກ",
    className: "border-slate-200 bg-slate-50 text-slate-600",
    icon: FiXCircle,
  },
};

const getResponseTotalCount = (response: any) => {
  const payload = response?.data ?? response;
  const count =
    payload?.count ??
    payload?.total ??
    payload?.totalCount ??
    payload?.meta?.total ??
    response?.count ??
    response?.total ??
    response?.totalCount;
  const numericCount = Number(count);

  return Number.isFinite(numericCount) ? numericCount : undefined;
};

const getReferenceCode = (item: WithdrawRequest) =>
  item.code || item.id || "-";

const formatKip = (value: number) => `₭${formatCurrency(value)}`;

const formatSignedKip = (value: number) => {
  if (!value) return formatKip(0);
  return `${value < 0 ? "-" : ""}${formatKip(Math.abs(value))}`;
};

const formatHistoryDate = (value?: string) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  })
    .format(date)
    .replace(",", " ·");
};

const maskAccountNumber = (value?: string) => {
  const digits = String(value || "").replace(/\D/g, "");
  if (!digits) return "••••";
  return `•••• ${digits.slice(-4)}`;
};

const getBankTitle = (item: WithdrawRequest) =>
  item.bankName || item.bankAccountName || "ບັນຊີຮັບເງິນ";

const getBankSubtitle = (item: WithdrawRequest) => {
  const masked = maskAccountNumber(item.bankAccountNo);
  if (!item.bankAccountName) return masked;
  return `${item.bankAccountName} ${masked}`;
};

const getErrorMessage = (error: any) =>
  error?.response?.data?.message || error?.message || "ໂຫລດປະຫວັດການຖອນບໍ່ສຳເລັດ";

function SummaryCard({
  icon: Icon,
  iconClassName,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  iconClassName: string;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-lg border border-[#e6ebf1] bg-white px-4 py-4 shadow-[0_8px_24px_rgba(16,24,40,0.04)] sm:px-5">
      <div className="flex items-center gap-3">
        <span
          className={`flex h-10 w-10 flex-none items-center justify-center rounded-full ${iconClassName}`}
        >
          <Icon className="h-5 w-5" />
        </span>
        <div className="min-w-0">
          <p className="m-0 text-[12px] font-bold text-[#667085]">{label}</p>
          <p className="m-0 mt-1 truncate text-xl font-black text-[#101828] sm:text-2xl">
            {value}
          </p>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: WithdrawRequestStatus }) {
  const meta = statusMeta[status] || statusMeta.PENDING;
  const Icon = meta.icon;

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-bold ${meta.className}`}
    >
      <Icon className="h-3.5 w-3.5" />
      {meta.label}
    </span>
  );
}

function WithdrawHistorySkeleton() {
  return (
    <>
      {Array.from({ length: HISTORY_PAGE_SIZE }, (_, index) => (
        <tr key={`withdraw-history-skeleton-${index}`} className="border-b border-[#edf1f6]">
          {Array.from({ length: 7 }, (_, cellIndex) => (
            <td key={`${index}-${cellIndex}`} className="px-4 py-4">
              <div className="h-3 w-full max-w-[140px] animate-pulse rounded bg-slate-100" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

export default function WithdrawHistoryPage() {
  const [token] = useLocalStorage("token", "");
  const [businessId] = useLocalStorage("businessId", "");
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>("ALL");
  const [page, setPage] = React.useState(1);

  const {
    data,
    error,
    isError,
    isLoading,
    isFetching,
    refetch,
  } = useQuery<WithdrawHistoryQueryData, any>(
    ["withdraw-history-page", businessId || "streamer"],
    async () => {
      if (!token) return { items: [] };
      const response = await donateWithdrawGetMany({
        token,
        businessId,
        skip: 0,
        limit: HISTORY_FETCH_LIMIT,
      });

      return {
        items: normalizeWithdrawRequests(response),
        totalCount: getResponseTotalCount(response),
      };
    },
    { enabled: !!token, retry: false },
  );

  const history = React.useMemo(
    () =>
      [...(data?.items || [])].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      ),
    [data?.items],
  );

  const statusCounts = React.useMemo(
    () =>
      history.reduce(
        (counts, item) => {
          counts[item.status] += 1;
          return counts;
        },
        {
          PENDING: 0,
          SUCCESS: 0,
          FAILED: 0,
          CANCELED: 0,
        } as Record<WithdrawRequestStatus, number>,
      ),
    [history],
  );

  const summary = React.useMemo(
    () =>
      history.reduce(
        (totals, item) => {
          if (item.status === "SUCCESS") totals.successAmount += item.amount;
          if (item.status === "PENDING") totals.pendingCount += 1;
          totals.feeAmount += item.feeAmount;
          return totals;
        },
        { successAmount: 0, pendingCount: 0, feeAmount: 0 },
      ),
    [history],
  );

  const filterOptions: Array<{ value: StatusFilter; label: string; count: number }> = [
    { value: "ALL", label: "ທັງໝົດ", count: data?.totalCount ?? history.length },
    { value: "PENDING", label: "ກຳລັງດຳເນີນ", count: statusCounts.PENDING },
    { value: "SUCCESS", label: "ສຳເລັດ", count: statusCounts.SUCCESS },
    {
      value: "FAILED_OR_CANCELED",
      label: "ບໍ່ສຳເລັດ",
      count: statusCounts.FAILED + statusCounts.CANCELED,
    },
  ];

  const filteredHistory = React.useMemo(() => {
    if (statusFilter === "ALL") return history;
    if (statusFilter === "FAILED_OR_CANCELED") {
      return history.filter(
        (item) => item.status === "FAILED" || item.status === "CANCELED",
      );
    }

    return history.filter((item) => item.status === statusFilter);
  }, [history, statusFilter]);

  const totalPages = Math.max(
    Math.ceil(filteredHistory.length / HISTORY_PAGE_SIZE),
    1,
  );
  const pageStart = (page - 1) * HISTORY_PAGE_SIZE;
  const pageEnd = Math.min(pageStart + HISTORY_PAGE_SIZE, filteredHistory.length);
  const visibleHistory = filteredHistory.slice(pageStart, pageEnd);

  React.useEffect(() => {
    setPage(1);
  }, [statusFilter]);

  React.useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  return (
    <div className="w-full min-w-0">
      <div className="grid gap-3 md:grid-cols-3">
        <SummaryCard
          icon={FiArrowDown}
          iconClassName="bg-emerald-50 text-emerald-500"
          label="ຍອດຖອນສຳເລັດ"
          value={formatKip(summary.successAmount)}
        />
        <SummaryCard
          icon={FiClock}
          iconClassName="bg-cyan-50 text-cyan-500"
          label="ກຳລັງດຳເນີນການ"
          value={String(summary.pendingCount)}
        />
        <SummaryCard
          icon={FiCreditCard}
          iconClassName="bg-amber-50 text-amber-500"
          label="ຄ່າທຳນຽມລວມ"
          value={formatKip(summary.feeAmount)}
        />
      </div>

      <div className="mt-4 overflow-hidden rounded-lg border border-[#e6ebf1] bg-white shadow-[0_10px_28px_rgba(16,24,40,0.05)]">
        <div className="flex flex-col gap-3 border-b border-[#edf1f6] px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <h3 className="m-0 text-sm font-black text-[#101828]">
              ປະຫວັດການຖອນເງິນ
            </h3>
            {isError && (
              <p className="m-0 mt-1 text-xs font-semibold text-red-600">
                {getErrorMessage(error)}
              </p>
            )}
          </div>

          <div className="flex max-w-full flex-wrap items-center gap-2">
            <div className="flex max-w-full flex-wrap items-center gap-1 rounded-lg border border-[#e6ebf1] bg-[#f8fafc] p-1">
              {filterOptions.map((option) => {
                const isActive = statusFilter === option.value;

                return (
                  <button
                    key={option.value}
                    type="button"
                    className={`h-8 rounded-md px-3 text-xs font-bold transition ${
                      isActive
                        ? "bg-white text-[#101828] shadow-sm"
                        : "text-[#667085] hover:text-[#168feb]"
                    }`}
                    onClick={() => setStatusFilter(option.value)}
                  >
                    {option.label}{" "}
                    <span className={isActive ? "text-[#667085]" : "text-[#98a2b3]"}>
                      {option.count}
                    </span>
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#d9e3ef] bg-white text-[#475467] transition hover:border-[#168feb] hover:text-[#168feb]"
              onClick={() => refetch()}
              disabled={isFetching}
              aria-label="Refresh withdraw history"
            >
              <FiRefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[1060px] border-collapse text-left">
            <thead>
              <tr className="border-b border-[#edf1f6] bg-[#fbfcfe] text-[12px] font-bold text-[#667085]">
                <th className="px-4 py-3">ວັນທີ · ເວລາ</th>
                <th className="px-4 py-3">ອ້າງອີງ</th>
                <th className="px-4 py-3">ທະນາຄານ</th>
                <th className="px-4 py-3 text-right">ຈຳນວນເງິນ</th>
                <th className="px-4 py-3 text-right">ຄ່າທຳນຽມ</th>
                <th className="px-4 py-3 text-right">ໄດ້ຮັບສຸດທິ</th>
                <th className="px-4 py-3 text-center">ສະຖານະ</th>
              </tr>
            </thead>
            <tbody className="text-[13px] text-[#344054]">
              {isLoading ? (
                <WithdrawHistorySkeleton />
              ) : visibleHistory.length ? (
                visibleHistory.map((item) => (
                  <tr
                    key={item.id}
                    className="border-b border-[#edf1f6] transition hover:bg-[#f8fbff]"
                  >
                    <td className="whitespace-nowrap px-4 py-3 text-[#667085]">
                      {formatHistoryDate(item.createdAt)}
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-[12px] font-semibold text-[#667085]">
                        {getReferenceCode(item)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex min-w-0 items-center gap-2">
                        <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-[#e8f4ff] text-[#168feb]">
                          <FiCreditCard className="h-4 w-4" />
                        </span>
                        <div className="min-w-0">
                          <p className="m-0 truncate text-[12px] font-black text-[#101828]">
                            {getBankTitle(item)}
                          </p>
                          <p className="m-0 mt-0.5 truncate text-[11px] font-semibold text-[#98a2b3]">
                            {getBankSubtitle(item)}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right font-black text-[#101828]">
                      {formatKip(item.amount)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right font-semibold text-[#667085]">
                      {formatSignedKip(-Math.abs(item.feeAmount))}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right font-black text-[#101828]">
                      {formatKip(getWithdrawNetAmount(item))}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-center">
                      <StatusBadge status={item.status} />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <div className="mx-auto flex max-w-sm flex-col items-center">
                      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#eef7ff] text-[#168feb]">
                        <FiInbox className="h-6 w-6" />
                      </span>
                      <p className="m-0 mt-3 text-sm font-black text-[#101828]">
                        ບໍ່ພົບປະຫວັດການຖອນ
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="m-0 text-xs font-semibold text-[#667085]">
            ສະແດງ {filteredHistory.length ? pageStart + 1 : 0}-{pageEnd} ຈາກ{" "}
            {filteredHistory.length}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#d9e3ef] bg-white text-[#475467] transition hover:border-[#168feb] hover:text-[#168feb] disabled:cursor-not-allowed disabled:opacity-40"
              disabled={page <= 1}
              onClick={() => setPage((currentPage) => Math.max(currentPage - 1, 1))}
              aria-label="Previous page"
            >
              <FiChevronLeft className="h-4 w-4" />
            </button>
            <span className="min-w-[72px] text-center text-xs font-black text-[#101828]">
              {page} / {totalPages}
            </span>
            <button
              type="button"
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#d9e3ef] bg-white text-[#475467] transition hover:border-[#168feb] hover:text-[#168feb] disabled:cursor-not-allowed disabled:opacity-40"
              disabled={page >= totalPages}
              onClick={() =>
                setPage((currentPage) => Math.min(currentPage + 1, totalPages))
              }
              aria-label="Next page"
            >
              <FiChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
